import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { QueryStatus, QueryType, QueryWithExecutionRecord } from '../../../../../API';
import { WorkbenchHistoryRecord } from '../../../models/history-model';
import { QueryHistoryService } from '../../../services/query-history.service';

@Component({
  selector: 'app-scheduler-history',
  templateUrl: './scheduler-history.component.html',
  styleUrl: './scheduler-history.component.scss',
})
export class SchedulerHistoryComponent {
  readonly templatedQueryEditTooltip = 'Editing a templated query is not allowed, please check with the administrator.';

  @Input() searchTerm = '';
  @Input() rows: WorkbenchHistoryRecord[] = [];
  @Output() deleteEvent: EventEmitter<any> = new EventEmitter<any>();

  headers: any[] = [];
  isViewModalVisible = false;
  isViewModalLoading = false;
  selectedRow: WorkbenchHistoryRecord | null = null;
  selectedQueryRecord: QueryWithExecutionRecord | null = null;
  selectedQueryText = '';

  constructor(
    private queryHistoryService: QueryHistoryService,
    private router: Router,
  ) {
    this.setHeaders();
  }

  get filteredRows(): WorkbenchHistoryRecord[] {
    return this.rows.filter((row) => this.matchesSearch(row));
  }

  setHeaders(): void {
    this.headers = [
      {
        name: 'Scheduler Name',
        width: '17.5rem',
        sortOrder: null,
        sortFn: (a: WorkbenchHistoryRecord, b: WorkbenchHistoryRecord) =>
          a.schedulerName.localeCompare(b.schedulerName),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Query ID',
        width: '13.75rem',
        sortOrder: null,
        sortFn: (a: WorkbenchHistoryRecord, b: WorkbenchHistoryRecord) => a.queryId.localeCompare(b.queryId),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Query Name',
        width: '13.75rem',
        sortOrder: null,
        sortFn: (a: WorkbenchHistoryRecord, b: WorkbenchHistoryRecord) => a.queryName.localeCompare(b.queryName),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Status',
        width: '8.75rem',
        sortOrder: null,
        sortFn: (a: WorkbenchHistoryRecord, b: WorkbenchHistoryRecord) => a.status.localeCompare(b.status),
        filterMultiple: true,
        filters: [
          { text: 'Draft', value: 'DRAFT' },
          { text: 'Success', value: 'EXECUTED' },
          { text: 'In-progress', value: 'IN_PROGRESS' },
          { text: 'Validated', value: 'VALIDATED' },
          { text: 'Validation Failed', value: 'VALIDATION_FAILED' },
          { text: 'Failed', value: 'FAILED' },
        ],
        filterFn: (selectedStatuses: QueryStatus[], item: WorkbenchHistoryRecord) =>
          !selectedStatuses.length || selectedStatuses.includes(item.status),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Last Modified On',
        width: '12.5rem',
        sortOrder: 'descend',
        sortFn: (a: WorkbenchHistoryRecord, b: WorkbenchHistoryRecord) =>
          new Date(a.lastModifiedAt).getTime() - new Date(b.lastModifiedAt).getTime(),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Last Modified By',
        width: '12.5rem',
        sortOrder: null,
        sortFn: (a: WorkbenchHistoryRecord, b: WorkbenchHistoryRecord) =>
          a.lastModifiedBy.localeCompare(b.lastModifiedBy),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Action',
        width: '7rem',
        right: '0',
        sortOrder: null,
        sortFn: null,
        sortDirections: [],
      },
    ];
  }

  getStatusColor(status: QueryStatus): string {
    return this.queryHistoryService.getStatusColor(status);
  }

  getDisplayStatus(status: QueryStatus): string {
    return this.queryHistoryService.getDisplayStatus(status);
  }

  trackByHeader(index: number, item: { name: string }): string {
    return item.name;
  }

  onEditClick(row: WorkbenchHistoryRecord): void {
    if (row.queryType === QueryType.STANDARD) {
      return;
    }

    this.router.navigate([`/market-fan-exploration/edit/STANDARD/${row.queryId}/${row.executionId}`]);
  }

  async openViewQueryModal(row: WorkbenchHistoryRecord): Promise<void> {
    this.selectedRow = row;
    this.selectedQueryRecord = null;
    this.selectedQueryText = '';
    this.isViewModalLoading = true;
    this.isViewModalVisible = true;

    try {
      const queryRecord = await this.queryHistoryService.getQueryRecordById(row.queryId, row.executionId);

      if (this.selectedRow?.queryId !== row.queryId || this.selectedRow?.executionId !== row.executionId) {
        return;
      }

      this.selectedQueryRecord = queryRecord;
      this.selectedQueryText = queryRecord?.query?.trim() || '-- Query text is not available for this execution.';
    } catch (error) {
      console.error('Failed to load query details', error);

      if (this.selectedRow?.queryId === row.queryId && this.selectedRow?.executionId === row.executionId) {
        this.selectedQueryText = '-- Failed to load query text.';
      }
    } finally {
      if (this.selectedRow?.queryId === row.queryId && this.selectedRow?.executionId === row.executionId) {
        this.isViewModalLoading = false;
      }
    }
  }

  closeViewQueryModal(): void {
    this.isViewModalVisible = false;
    this.isViewModalLoading = false;
    this.selectedRow = null;
    this.selectedQueryRecord = null;
    this.selectedQueryText = '';
  }

  private matchesSearch(row: WorkbenchHistoryRecord): boolean {
    const searchValue = this.searchTerm.trim().toLowerCase();

    if (!searchValue) {
      return true;
    }

    return [
      row.queryId,
      row.queryName,
      row.status,
      row.querySource,
      row.schedulerName,
      row.lastModifiedAt,
      row.lastModifiedBy,
    ].some((value) => value.toLowerCase().includes(searchValue));
  }

  delete(row: WorkbenchHistoryRecord): void {
    this.deleteEvent.emit(row);
  }
  canEditScheduleHistory(row: WorkbenchHistoryRecord): boolean {
    return (
      row?.status === QueryStatus.EXECUTED ||
      row?.status === QueryStatus.IN_PROGRESS ||
      row?.queryType === QueryType.STANDARD
    );
  }
}
